import { FC } from 'react'

import { BankingPaymentFilter } from 'generated/resources/datatable.graphql'
import {
  BankingTransferFilter,
  PaymentsAmountWidgetDocument,
  PaymentsAmountWidgetQuery,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import { removeNullBetween } from 'modules/finance/components/Widgets/common/utils'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

interface PreparedDataArgsType {
  amountFormatter: (value: number, currency?: string) => string
}

interface QueryArgsType {}

const prepareData = (
  data: PaymentsAmountWidgetQuery,
  prepareDataArgs: PreparedDataArgsType,
  amountCurrency?: string[]
) => {
  const [firstCurrency, ...others] = amountCurrency || []
  const currenciesFromPayments =
    data?.banking?.paymentsConnection?.aggregate?.terms?.currency?.values || []
  const currenciesFromTransfers =
    data?.banking?.transfersConnection?.aggregate?.terms?.currency?.values || []

  const [firstCurrencyFromAggregates, ...othersFromAggregates] = [
    ...new Set([...currenciesFromPayments, ...currenciesFromTransfers]),
  ]

  if (!!others.length || !!othersFromAggregates.length) {
    return {
      countersChartDatas: [
        {
          label: i18n.t('finance.overview.outstanding_payments.total'),
          value: 0,
          formattedValue: '--',
        },
      ],
    }
  }

  const paymentsAmount = data?.banking?.paymentsConnection?.aggregate?.sum?.amountCents || 0
  const transfersAmount = data?.banking?.transfersConnection?.aggregate?.sum?.amountCents || 0
  const amount = paymentsAmount + transfersAmount

  return {
    countersChartDatas: [
      {
        label: i18n.t('finance.overview.outstanding_payments.total'),
        value: amount,
        formattedValue: prepareDataArgs.amountFormatter(
          amount,
          firstCurrency || firstCurrencyFromAggregates
        ),
      },
    ],
  }
}

const buildTransferDataTableFiltersFromPaymentFilters = (
  filters: BankingPaymentFilter
): BankingTransferFilter => {
  const { date: createdAt, dueDate } = filters

  return { createdAt, dueDate }
}

export const buildPaymentsAmountWidget = ({
  widgetTitle,
  queryArgs,
  prepareDataArgs,
  filters,
}: {
  widgetTitle?: string
  queryArgs?: QueryArgsType
  prepareDataArgs: PreparedDataArgsType
  filters: BankingPaymentFilter
}) => {
  const PaymentsAmountWidget: FC<{
    data: ReturnType<typeof prepareData>
  }> = ({ data }) => {
    const { countersChartDatas } = data

    return (
      <FinanceWidget
        id={`paymentsAmountDashboard-${widgetTitle}`}
        type={SupportedCharts.COUNTERS}
        showChart={false}
        countersChartDatas={countersChartDatas.map((chartData) => ({
          ...chartData,
          label: widgetTitle || chartData.label,
        }))}
      />
    )
  }

  // This is to avoid problems with date filter and between provoked by the use of datatable filters
  const filteredPaymentsFilters = removeNullBetween(filters)
  const filteredTransferFilters = removeNullBetween(
    buildTransferDataTableFiltersFromPaymentFilters(filteredPaymentsFilters)
  )
  const filterCurrencies = filters.currency?.in?.flatMap((currency) => currency)

  return buildWidget({
    id: `paymentsAmountWidget-${widgetTitle}`,
    component: PaymentsAmountWidget,
    document: PaymentsAmountWidgetDocument,
    prepareData: (data: PaymentsAmountWidgetQuery) =>
      prepareData(data, prepareDataArgs, filterCurrencies),
    queryArgs: {
      ...queryArgs,
      paymentsFilters: { ...filteredPaymentsFilters },
      transfersFilters: { ...filteredTransferFilters },
    },
  })
}
